import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { joyApi } from "../api/joyApi";

const AuthCtx = createContext(null);
const TEST_ALLOWED = process.env.REACT_APP_ALLOW_TEST_LOGIN === "true";
// TEMPORARY: when true, the Web App is accessible directly via link (no email/code login).
// The full real-login structure below is preserved and returns automatically when this is false.
const PUBLIC_ACCESS = process.env.REACT_APP_PUBLIC_ACCESS === "true";

const KEY_TOKEN = "joy_auth_token";
const KEY_USER = "joy_auth_user_id";
const KEY_EMAIL = "joy_auth_email";
const KEY_PUBLIC = "joy_public_user_id";

function ensurePublicId() {
  let id = localStorage.getItem(KEY_PUBLIC);
  if (!id) {
    const rand =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}${Math.random().toString(16).slice(2)}`;
    id = `web_${rand}`;
    localStorage.setItem(KEY_PUBLIC, id);
  }
  return id;
}

function clearAuth() {
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_USER);
  localStorage.removeItem(KEY_EMAIL);
}

const LOGGED_OUT = { loading: false, authenticated: false, userId: null, email: null };

export function AuthProvider({ children }) {
  const [state, setState] = useState({ loading: true, authenticated: false, userId: null, email: null });

  const restore = useCallback(async () => {
    if (PUBLIC_ACCESS) {
      setState({ loading: false, authenticated: true, userId: ensurePublicId(), email: null });
      return;
    }
    const token = localStorage.getItem(KEY_TOKEN);
    if (!token) {
      setState(LOGGED_OUT);
      return;
    }
    if (token === "TEST_SESSION" && TEST_ALLOWED) {
      setState({
        loading: false,
        authenticated: true,
        userId: localStorage.getItem(KEY_USER) || "web_test_user",
        email: localStorage.getItem(KEY_EMAIL) || "teste@joy.dev",
      });
      return;
    }
    try {
      const s = await joyApi.session(token);
      if (s.authenticated && s.user_id) {
        localStorage.setItem(KEY_USER, s.user_id);
        if (s.email) localStorage.setItem(KEY_EMAIL, s.email);
        setState({ loading: false, authenticated: true, userId: s.user_id, email: s.email });
      } else {
        clearAuth();
        setState(LOGGED_OUT);
      }
    } catch (e) {
      // Network error: keep user out but don't wipe token (could be transient)
      setState(LOGGED_OUT);
    }
  }, []);

  useEffect(() => {
    restore();
  }, [restore]);

  const login = (token, userId, email) => {
    localStorage.setItem(KEY_TOKEN, token);
    localStorage.setItem(KEY_USER, userId);
    if (email) localStorage.setItem(KEY_EMAIL, email);
    setState({ loading: false, authenticated: true, userId, email });
  };

  const testLogin = () => login("TEST_SESSION", "web_test_user", "teste@joy.dev");

  const logout = async () => {
    if (PUBLIC_ACCESS) {
      // Public-access mode has no real login; keep the same anonymous profile.
      setState({ loading: false, authenticated: true, userId: ensurePublicId(), email: null });
      return;
    }
    const token = localStorage.getItem(KEY_TOKEN);
    try {
      if (token && token !== "TEST_SESSION") await joyApi.logout(token);
    } catch (e) {
      /* ignore */
    }
    clearAuth();
    setState(LOGGED_OUT);
  };

  return (
    <AuthCtx.Provider value={{ ...state, login, testLogin, logout, testAllowed: TEST_ALLOWED }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
